import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { useDebounce } from 'use-debounce';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { playlistsApi } from '../../repositories/playlists';
import type { PlaylistMeta } from '../../repositories/playlists';
import { countryCodes } from '../../pages/analytics/Data/dropdown';
import { playlistCountLabel } from '../playlists/playlistCountLabel';
import { PlaylistFreshness } from '../playlists/PlaylistFreshness';
import { CurrentPositionCell, EntryDateCell } from '../playlists/PlaylistDateCells';
import {
  StaleDividerRow,
  staleArtworkClass,
  staleTextClass,
  startsStaleGroup,
} from '../playlists/stalePlacements';
import AnalyticsExportModal from '../modal/AnalyticsExportModal';
import Pagination from '../common/Pagination';
import { Skeleton } from '../ui/skeleton';
import Button from '../ui/Button';

interface ArtistPlaylistProps {
  artistUUID?: string;
  artistName?: string;
}

type PlaylistType = 'editorial' | 'algorithmic' | 'algotorial';

// A display page size. The Cadence backend fetches the artist's complete result set from
// SoundCharts and deduplicates across all of it before paging, so this is only how many
// deduplicated rows we show at a time.
const PLAYLIST_PAGE_SIZE = 10;

// siriusxm is deliberately absent: SoundCharts has no playlist endpoint for it and answered every
// artist with HTTP 400, which the tab swallowed and rendered as an empty result. The backend
// rejects it outright now, so offering the button would only produce a validation error.
// SiriusXM remains a DSP in the pitch form, which is a separate feature.
const PLAYLIST_PLATFORMS = [
  'spotify',
  'amazon',
  'deezer',
  'tidal',
  'apple-music',
  'youtube',
  'audiomack',
  'soundcloud',
];

const platformDisplayNames: { [key: string]: string } = {
  spotify: 'Spotify',
  amazon: 'Amazon Music',
  deezer: 'Deezer',
  tidal: 'TIDAL',
  'apple-music': 'Apple Music',
  youtube: 'YouTube',
  audiomack: 'Audiomack',
  soundcloud: 'SoundCloud',
};

// Only Spotify distinguishes algorithmic/algotorial placements; every other DSP is editorial.
const PLAYLIST_TYPES: PlaylistType[] = ['editorial', 'algorithmic', 'algotorial'];

// These platforms report no follower counts, so the column would read 0 on every row.
const PLATFORMS_WITHOUT_FOLLOWERS = ['apple-music', 'amazon', 'tidal'];

// The roster shows current placements only. Its history is the pitch view's job: the
// roster sync is current-only, so a historical tab here could only ever be partial.
const PLAYLIST_SCOPE = 1;

const formatDateValue = (value: string) => new Date(value).toLocaleDateString();
const formatNumber = (value?: number | null) =>
  value == null ? '—' : value.toLocaleString();
const formatPosition = (value?: number | null) => (value == null ? '—' : value);

const ArtistPlaylist: React.FC<ArtistPlaylistProps> = ({ artistUUID, artistName }) => {
  const [playlists, setPlaylists] = useState<any[]>([]);
  const [playlistsLoading, setPlaylistsLoading] = useState(false);
  const [playlistPlatform, setPlaylistPlatform] = useState<string>('spotify');
  const [filterType, setFilterType] = useState<PlaylistType>('editorial');
  const [selectedCountry, setSelectedCountry] = useState<{ value: string; label: string } | null>(null);
  const [playlistPage, setPlaylistPage] = useState(1);
  // Count of real placements after deduplication, reported by the backend.
  const [playlistTotal, setPlaylistTotal] = useState(0);
  // True when the backend's page cap stopped its walk early, so the set below is incomplete.
  const [playlistsCapped, setPlaylistsCapped] = useState(false);
  // The market the result is scoped to, which is not always the one selected above: the backend
  // applies a country of its own on Apple Music and Amazon, where a playlist is published once per
  // market. Null means the set spans every market.
  const [playlistCountry, setPlaylistCountry] = useState<string | null>(null);
  // Rows to page through. Stored data lists stale placements without counting them, so paging by
  // the count would leave the last of them unreachable.
  const [playlistPageTotal, setPlaylistPageTotal] = useState(0);
  // Where the data came from and how fresh it is; drives the "Data as of" line.
  const [playlistMeta, setPlaylistMeta] = useState<PlaylistMeta | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);
  // Bumped by a refresh to run the fetch effect again and pick up the new rows.
  const [refreshNonce, setRefreshNonce] = useState(0);
  // Set by a refresh so the one read after it skips the browser's cache; later reads use it.
  const bustNextReadRef = useRef(false);
  const [playlistSearch, setPlaylistSearch] = useState('');
  // The backend searches the artist's whole set, so a search is a request: wait for a pause in typing.
  const [debouncedTerm] = useDebounce(playlistSearch.trim(), 400);
  // Clearing the box applies at once, so changing platform, which clears it, is one request.
  const debouncedSearch = playlistSearch.trim() ? debouncedTerm : '';
  const [showExportModal, setShowExportModal] = useState(false);

  const countryOptions = useMemo(
    () => Object.entries(countryCodes).map(([code, name]) => ({ value: code, label: name })),
    []
  );

  // Non-Spotify platforms have no algorithmic/algotorial data, so fall back rather than issue a
  // request that can only come back empty.
  useEffect(() => {
    if (playlistPlatform !== 'spotify' && filterType !== 'editorial') {
      setFilterType('editorial');
    }
  }, [playlistPlatform, filterType]);

  // Anything that changes the result set goes back to its first page.
  useEffect(() => {
    setPlaylistPage(1);
  }, [playlistPlatform, filterType, selectedCountry, debouncedSearch, artistUUID]);

  useEffect(() => {
    if (!artistUUID) return;
    if (playlistPlatform !== 'spotify' && filterType !== 'editorial') return;

    let cancelled = false;

    const fetchPlaylists = async () => {
      setPlaylistsLoading(true);
      const bustBrowserCache = bustNextReadRef.current;
      bustNextReadRef.current = false;
      try {
        const res = await playlistsApi.getPlaylists(
          'artist',
          artistUUID,
          playlistPlatform,
          {
            currentOnly: PLAYLIST_SCOPE,
            type: filterType,
            page: playlistPage,
            limit: PLAYLIST_PAGE_SIZE,
            ...(selectedCountry ? { countryCode: selectedCountry.value } : {}),
            search: debouncedSearch,
            // After a refresh the URL is the one just fetched; skip the browser's cached copy.
            bustBrowserCache,
          }
        );
        if (cancelled) return;

        const items = (res?.data?.data || []).map((item: any) => ({ ...item, _platform: playlistPlatform }));
        const meta = res?.data?.meta;

        setPlaylists(items);
        setPlaylistTotal(meta?.totalPlaylists ?? items.length);
        setPlaylistPageTotal(res?.data?.pagination?.totalItems ?? meta?.totalPlaylists ?? items.length);
        setPlaylistsCapped(Boolean(meta?.capped));
        setPlaylistCountry(meta?.countryCode ?? null);
        setPlaylistMeta(meta ?? null);
      } catch (e) {
        if (!cancelled) {
          setPlaylists([]);
          setPlaylistTotal(0);
          setPlaylistPageTotal(0);
          setPlaylistsCapped(false);
          setPlaylistCountry(null);
          setPlaylistMeta(null);
        }
      } finally {
        if (!cancelled) setPlaylistsLoading(false);
      }
    };

    fetchPlaylists();
    return () => {
      cancelled = true;
    };
  }, [artistUUID, playlistPlatform, filterType, selectedCountry, debouncedSearch, playlistPage, refreshNonce]);

  // A refresh message belongs to the platform and tab it was about.
  useEffect(() => {
    setRefreshMessage(null);
  }, [playlistPlatform, filterType]);

  /**
   * Syncs this artist on this platform from SoundCharts, then reads the table again. It cannot
   * beat SoundCharts' own crawl, and the backend allows one per artist and platform per five
   * minutes.
   */
  const handleRefresh = async () => {
    if (!artistUUID || refreshing) return;

    setRefreshing(true);
    setRefreshMessage(null);

    try {
      const res = await playlistsApi.refreshPlaylists('artist', artistUUID, playlistPlatform);
      if (res?.data?.message) setRefreshMessage(res.data.message);
      bustNextReadRef.current = true;
      setRefreshNonce((n) => n + 1);
    } catch (error: any) {
      setRefreshMessage(error?.response?.data?.message || 'Could not refresh. Try again shortly.');
    } finally {
      setRefreshing(false);
    }
  };

  // `playlists` already holds exactly one page from the API, deduplicated and searched across the
  // artist's complete set, so it IS the page to render. Page count comes from the API's total.
  const totalPages = Math.max(1, Math.ceil(playlistPageTotal / PLAYLIST_PAGE_SIZE));
  const pageRows = playlists;

  if (!artistUUID) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-neutral-500">
          This artist has no SoundCharts profile linked, so playlist data is unavailable.
        </p>
      </div>
    );
  }

  const availableTypes = playlistPlatform === 'spotify' ? PLAYLIST_TYPES : (['editorial'] as PlaylistType[]);
  const showFollowers = !PLATFORMS_WITHOUT_FOLLOWERS.includes(playlistPlatform);
  // Playlist, Song, Peak, Current Position, Entry, + Followers where the platform has them.
  const colSpan = showFollowers ? 6 : 5;

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="mb-4 text-xl font-semibold text-black">
          Playlist Analytics by Platform
        </h2>
        <Button onClick={() => setShowExportModal(true)}>
          <ArrowDownTrayIcon className="w-4 h-4" />
          Export
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {PLAYLIST_PLATFORMS.map((platform) => (
          <button
            key={platform}
            onClick={() => {
              setPlaylistPlatform(platform);
              setPlaylistSearch('');
            }}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors ${playlistPlatform === platform
              ? 'bg-primary-700 hover:bg-primary-800 text-white'
              : 'bg-neutral-100 text-neutral-700 hover:bg-primary-50 hover:text-primary-700'
              }`}
          >
            {platformDisplayNames[platform] || platform}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Search playlist or song name"
            value={playlistSearch}
            onChange={(e) => setPlaylistSearch(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md sm:w-80 focus:outline-none focus:border-primary-500"
          />
        </div>
      </div>

      <div className="flex mb-4 space-x-4">
        {availableTypes.map((type) => (
          <label key={type} className="flex items-center py-2 space-x-2 cursor-pointer">
            <input
              type="radio"
              value={type}
              checked={filterType === type}
              onChange={() => setFilterType(type)}
              className="text-primary-700 form-radio"
            />
            <span className={`text-sm ${filterType === type ? 'text-primary-700 font-semibold' : 'text-neutral-700'}`}>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </span>
          </label>
        ))}
      </div>

      <div className="mb-4">
        <label className="block mb-2 text-sm font-medium text-neutral-700">
          Filter by Country
        </label>
        <Select
          isClearable
          options={countryOptions}
          value={selectedCountry}
          onChange={(selected: any) => setSelectedCountry(selected)}
          className="react-select-container"
          classNamePrefix="react-select"
          placeholder="Select a country..."
          styles={{ menuList: (base) => ({ ...base, maxHeight: 200, overflowY: 'auto' }) }}
        />
      </div>

      <div className="flex items-center justify-between w-full mb-4">
        <h3 className="text-lg font-semibold text-black">
          Current{' '}
          {filterType.charAt(0).toUpperCase() + filterType.slice(1)}{' '}
          Playlists on {platformDisplayNames[playlistPlatform]}
        </h3>
      </div>

      {/* A count of real placements, deduplicated on the backend across the artist's complete
          result set, with when SoundCharts collected them and a way to fetch newer. */}
      {playlistsLoading ? (
        <Skeleton className="w-32 h-4 mb-2" />
      ) : (
        <PlaylistFreshness
          countLabel={playlistCountLabel(
            playlistTotal,
            playlistsCapped,
            playlistCountry,
            playlistMeta?.staleCount
          )}
          meta={playlistMeta}
          onRefresh={handleRefresh}
          refreshing={refreshing}
          refreshMessage={refreshMessage}
        />
      )}

      <div
        className="overflow-x-auto bg-white border rounded-lg border-neutral-200"
        aria-busy={playlistsLoading}
      >
        <table className="min-w-full">
          <thead>
            <tr className="bg-neutral-100 border-b border-neutral-200">
              <th className="px-4 py-3 text-sm font-semibold text-left text-neutral-700">Playlist</th>
              <th className="px-4 py-3 text-sm font-semibold text-left text-neutral-700">Song Title</th>
              {showFollowers && (
                <th className="px-4 py-3 text-sm font-semibold text-left text-neutral-700">Follower Count</th>
              )}
              <th className="px-4 py-3 text-sm font-semibold text-left text-neutral-700">Peak Position</th>
              <th className="px-4 py-3 text-sm font-semibold text-left text-neutral-700">Current Position</th>
              <th className="px-4 py-3 text-sm font-semibold text-left text-neutral-700">Entry Date</th>
            </tr>
          </thead>
          <tbody>
            {playlistsLoading ? (
              /*
                Shaped like the real rows — the first cell pairs a 56px artwork
                thumbnail with its title — so the table keeps its height and
                column widths instead of collapsing to a single centred line and
                snapping back when the data lands.
              */
              Array.from({ length: 5 }).map((_, r) => (
                <tr key={r} className="border-b border-neutral-100 last:border-b-0">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="rounded-md shrink-0 w-14 h-14" />
                      <Skeleton className="w-32 h-3.5" />
                    </div>
                  </td>
                  {Array.from({ length: colSpan - 1 }).map((_, c) => (
                    <td key={c} className="px-4 py-3">
                      <Skeleton className="w-20 h-3.5" />
                    </td>
                  ))}
                </tr>
              ))
            ) : playlists.length === 0 ? (
              <tr><td colSpan={colSpan} className="px-4 py-6 text-center text-neutral-500">
                {/* Before the first sync there is nothing to say about placements either way. */}
                {debouncedSearch
                  ? 'No playlists match your search.'
                  : playlistMeta?.syncStatus === 'never_synced'
                    ? 'No playlist data collected yet.'
                    : playlistMeta?.countryCovered === false
                      ? 'No stored playlist data for this country.'
                      : 'Artist is not currently on any playlists for this platform.'}
              </td></tr>
            ) : (
              pageRows.map((item, idx) => {
                // Stale rows are greyed under their own divider, not struck out: many of those
                // songs are still on the playlist, SoundCharts just has not re-confirmed them.
                const cellClass = `px-4 py-3 text-sm ${item.isStale ? staleTextClass : 'text-neutral-700'}`;
                return (
                  <React.Fragment key={`${item.playlist?.uuid || idx}-${item.song?.uuid || idx}-${idx}`}>
                    {startsStaleGroup(pageRows, idx) && (
                      <StaleDividerRow colSpan={colSpan} count={playlistMeta?.staleCount ?? 0} />
                    )}
                    <tr className="border-b border-neutral-100 hover:bg-neutral-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img
                            src={item.playlist?.imageUrl}
                            alt={item.playlist?.name}
                            className={`object-cover bg-neutral-100 rounded-md shadow-sm w-14 h-14 ${item.isStale ? staleArtworkClass : ''}`}
                            onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                          />
                          <span
                            className={`max-w-xs text-sm truncate ${item.isStale ? staleTextClass : 'text-black'}`}
                            title={item.playlist?.name}
                          >
                            {item.playlist?.name || '—'}
                          </span>
                        </div>
                      </td>
                      <td className={cellClass}>{item.song?.name || '—'}</td>
                      {showFollowers && (
                        <td className={cellClass}>{formatNumber(item.playlist?.latestSubscriberCount)}</td>
                      )}
                      <td className={cellClass}>{formatPosition(item.peakPosition)}</td>
                      <td className={cellClass}>
                        <CurrentPositionCell
                          item={item}
                          formatDate={formatDateValue}
                          staleAfterDays={playlistMeta?.staleAfterDays}
                        />
                      </td>
                      <td className={cellClass}>
                        <EntryDateCell item={item} formatDate={formatDateValue} />
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4">
        <Pagination
          page={playlistPage}
          totalPages={totalPages}
          setPage={setPlaylistPage}
          totalItems={playlistPageTotal}
          limit={PLAYLIST_PAGE_SIZE}
        />
      </div>

      <AnalyticsExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        availablePlatforms={PLAYLIST_PLATFORMS}
        platformDisplayNames={platformDisplayNames}
        uuid={null}
        scope="artist"
        artistUUID={artistUUID}
        defaultPlatform={playlistPlatform}
        defaultType={filterType}
        defaultDataScope={PLAYLIST_SCOPE}
        currentOnly
        artistName={artistName}
        selectedCountries={selectedCountry ? [selectedCountry.value] : []}
      />
    </div>
  );
};

export default ArtistPlaylist;
